import React from "react";
//#region redux
import { AppState } from "store";
import { useSelector } from "react-redux";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceConstants } from "utils/constants";
import { InvoiceFormTypes } from "models/formTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { CurrencyEnums, DimensionEnums } from "models/enums";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import useDefaultDeliverySettings from "../settings/useInvoiceDefaultDeliverySettings";

function useInvoiceInitialValues(invoice?: WintSuperkollAPIModel.Invoice | WintSuperkollAPIModel.RecurringInvoice) {
  const { dimensions } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);
  const allowedDimensionIds = React.useMemo(() => dimensions.flatMap((d) => d.value).map((d) => d.id), [dimensions]);

  const noVat = useSelector((store: AppState) => store.auth.selectedCompany.noVat);
  const defaultInvoiceNotes = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoiceNotes);
  const defaultInvoiceLayout = useSelector((store: AppState) => store.auth.selectedCompany.defaultInvoiceLayout);
  const defaultInvoicePaymentTerms = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoicePaymentTerms
  );

  const currentUser = useSelector(
    (store: AppState) => store.auth.loggedInUser,
    (left, right) => left.personId === right.personId
  );

  const { getDefaultDeliverySettings } = useDefaultDeliverySettings();

  const getDraftInitialValues = () => {
    const {
      reminderCost,
      isAutomaticRemindersEnabled,
      automaticReminderPeriod,
      automaticReminderDelay,
      automaticReminderAmountLimit,
      automaticReminderCountLimit,
      invoiceReminderNotesTemplate,
      automaticReminderSendType,
      invoiceReminderTitleTemplate,
      invoiceReminderMessageTemplate,
      invoiceTitleTemplate,
      invoiceMessageTemplate,
      invoiceSendType,
    } = getDefaultDeliverySettings();
    const obj: InvoiceFormTypes.Create = {
      products: [],
      ownerId: currentUser.personId,
      currency: CurrencyEnums.CurrencyCode.SEK,
      isDimensionsAdvancedConfiguration: false,
      isPeriodisationAdvancedConfiguration: false,
      isPeriodisationByDate: false,
      loadingCustomer: null,
      rutRot: {
        houseWorkOtherCosts: 0,
      },
      otherSettings: {
        invoiceDate: DateHelper.today(),
        deliveryDate: DateHelper.today(),
        paymentTerms: defaultInvoicePaymentTerms,
        dueDate: DateHelper.addDays(DateHelper.today(), defaultInvoicePaymentTerms),
        invoiceLayout: defaultInvoiceLayout,
        languageKey: InvoiceConstants.DefaultInvoiceLanguageCode,
        sellerName: currentUser.name,
        notes: defaultInvoiceNotes,
        reminderCost: reminderCost,
        isAutomaticRemindersEnabled: isAutomaticRemindersEnabled,
        automaticReminderPeriod: automaticReminderPeriod,
        automaticReminderDelay: automaticReminderDelay,
        automaticReminderAmountLimit: automaticReminderAmountLimit,
        automaticReminderCountLimit: automaticReminderCountLimit,
        automaticReminderNotes: invoiceReminderNotesTemplate,
        automaticReminderSendType: automaticReminderSendType,
        automaticReminderSendMail: {
          emailSubject: invoiceReminderTitleTemplate,
          emailMessage: invoiceReminderMessageTemplate,
        },
      },
      recurringSettings: {
        recurringOwner: { id: currentUser.personId, value: currentUser.name },
        recurringInvoiceStartDate: DateHelper.addDays(DateHelper.today(), 1),
        recurringInvoiceFrequency: WintSuperkollAPIModel.RecurringInvoiceFrequency.Monthly,
        recurringDeliveryAfterDays: 0,
        recurringInvoiceSendType: WintSuperkollAPIModel.RecurringInvoiceSendType.Manual,
        recurringInvoiceAutomaticSendType: WintSuperkollAPIModel.InvoiceSendType.SendForPrintOrEInvoice,
        recurringSendMail: {
          emailSubject: invoiceTitleTemplate,
          emailMessage: invoiceMessageTemplate,
        },
      },
      defaultSendType: invoiceSendType,
    };

    return obj;
  };

  const getRecurringInvoiceInitialValues = () => {
    let obj = getDraftInitialValues();

    if (invoice) {
      const recurringInvoice = invoice as WintSuperkollAPIModel.RecurringInvoice;
      const hasInvoiceRowDimensions = invoice.rows?.some(
        (row) => row.dimensions && row.dimensions.filter((d) => allowedDimensionIds.includes(d)).length > 0
      );

      obj = {
        ...obj,
        id: recurringInvoice.id,
        ownerId: recurringInvoice.ownerId,
        customer: recurringInvoice.customerId
          ? { id: recurringInvoice.customerId, value: recurringInvoice.customerName ?? "" }
          : undefined,
        referencePerson: recurringInvoice.customerReference?.referenceName
          ? {
              id: recurringInvoice.customerReference.referenceId,
              value: recurringInvoice.customerReference.referenceName ?? "",
            }
          : undefined,
        total: recurringInvoice.totalAmount - recurringInvoice.totalTax,
        tax: noVat ? 0 : recurringInvoice.totalTax,
        isEUCustomer: recurringInvoice.isEUCustomer,
        // NOTE: unlike InvoiceRow (hydrated below for regular invoices), RecurringInvoiceRow does
        // not carry articleAccount/articleVat/articleIsAgentSale, so for an opened recurring
        // invoice the VAT lock only kicks in once a row's article is reselected in this session.
        // isDomesticCustomer is still set by CustomerInformationLoader.
        products: recurringInvoice.rows?.map((product): InvoiceFormTypes.Product => {
          const standardPrice = product.overriddenUnitPrice ?? product.unitPrice ?? 0;
          const vat = noVat ? 0 : product.vat;
          return {
            guid: product.guid,
            uniqueId: product.guid,
            article: { id: product.articleId, value: product.description ?? product.overriddenDescription ?? "" },
            quantity: product.quantity,
            text: product.overriddenDescription ?? product.description,
            price: standardPrice,
            unit: product.unit?.id,
            tax: vat,
            taxText:
              vat === 0
                ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)
                : `${product.vat}%`,
            isEUCustomer: invoice.isEUCustomer,
            eUVatCategoryId: product.euVatProductType,
            dimensions: product.dimensions
              ? product.dimensions
                  .filter((d) => allowedDimensionIds.includes(d))
                  .concat(
                    recurringInvoice.dimensions?.filter(
                      (d) => allowedDimensionIds.includes(d) && !product.dimensions?.includes(d)
                    ) ?? []
                  )
              : recurringInvoice.dimensions?.filter((d) => allowedDimensionIds.includes(d)),
            recurringPeriodisation: {
              from: product.periodisationStartOffset,
              duration: product.periodisationLength,
              isEnabled: product.periodisation,
            },
          };
        }),
        currency: recurringInvoice.currency ?? CurrencyEnums.CurrencyCode.SEK,
        isPeriodisationAdvancedConfiguration: recurringInvoice.rows?.some(
          (row, index, arr) =>
            `${row.periodisation}${row.periodisationStartOffset}${row.periodisationLength}` !==
            `${arr[0].periodisation}${arr[0].periodisationStartOffset}${arr[0].periodisationLength}`
        ),
        isDimensionsAdvancedConfiguration: hasInvoiceRowDimensions,
        otherSettings: {
          ...obj.otherSettings,
          paymentTerms: recurringInvoice.paymentTerms,
          notes: recurringInvoice.notes,
          attachments: recurringInvoice.attachments as UploadedFile[],
          invoiceLayout: recurringInvoice.template ?? defaultInvoiceLayout,
          languageKey: recurringInvoice.language,
          showCurrencyCode: recurringInvoice.showCurrencyCode,
          showReferenceInAddress: recurringInvoice.showReferencePersonInAddress,
          showCustomerDeliveryAddress: recurringInvoice.showShippingAddress,
          sellerName: recurringInvoice.contactPerson,
          paymentMethodId: recurringInvoice.paymentOptionId,
        },
        recurringSettings: {
          ...obj.recurringSettings,
          recurringInvoiceStartDate: new Date(recurringInvoice.firstPostingDate),
          recurringRepeatUntilNotice: recurringInvoice.untilFurtherNotice,
          recurringInvoiceEndDate: recurringInvoice.stopDate ? new Date(recurringInvoice.stopDate) : undefined,
          recurringInvoiceFrequency: recurringInvoice.invoiceFrequency,
          recurringDescription: recurringInvoice.recurringDescription,
          recurringDeliveryAfterDays: recurringInvoice.daysToDelivery ?? 0,
          recurringOwner: { id: recurringInvoice.ownerId, value: recurringInvoice.ownerName ?? "" },
          recurringInvoiceSendType:
            recurringInvoice.recurringInvoiceSendType ?? WintSuperkollAPIModel.RecurringInvoiceSendType.Manual,
          recurringInvoiceAutomaticSendType:
            recurringInvoice.invoiceSendType ?? WintSuperkollAPIModel.InvoiceSendType.SendForPrintOrEInvoice,
          recurringSendMail:
            recurringInvoice.invoiceSendType === WintSuperkollAPIModel.InvoiceSendType.SendByEmail
              ? {
                  emailFromType:
                    recurringInvoice.mailFromType !== undefined
                      ? (recurringInvoice.mailFromType as unknown as WintSuperkollAPIModel.SendInvoiceMailsFromEnum)
                      : undefined,
                  emailTo: recurringInvoice.mailTo
                    ? UtilsHelper.splitMultipleEmails(recurringInvoice.mailTo).map((email) => ({
                        id: email,
                        value: email,
                      }))
                    : [],
                  emailSubject: recurringInvoice.mailTitle,
                  emailMessage: recurringInvoice.mailMessage,
                }
              : {},
        },
      };
    }

    return obj;
  };

  const getInvoiceInitialValues = () => {
    let obj = getDraftInitialValues();

    if (invoice) {
      const regularInvoice = invoice as WintSuperkollAPIModel.Invoice;
      const hasInvoiceRowDimensions = regularInvoice.rows?.some(
        (row) => row.dimensions && row.dimensions.filter((d) => allowedDimensionIds.includes(d)).length > 0
      );

      const houseWorkOtherCostsVAT = regularInvoice.rows?.length ? regularInvoice.rows[0].vat : 0;

      obj = {
        ...obj,
        id: regularInvoice.id,
        customer: regularInvoice.customerId
          ? { id: regularInvoice.customerId, value: regularInvoice.customerName ?? "" }
          : undefined,
        referencePerson: regularInvoice.customerReference?.referenceName
          ? {
              id: regularInvoice.customerReference.referenceId,
              value: regularInvoice.customerReference.referenceName ?? "",
            }
          : undefined,
        serialNumber:
          regularInvoice.serialNumber && regularInvoice.serialNumber > 0 ? regularInvoice.serialNumber : undefined,
        quotationSerialNumber: regularInvoice.quotationSerialNumber,
        ownerId: regularInvoice.ownerId,
        total: regularInvoice.totalAmount - regularInvoice.totalTax,
        tax: noVat ? 0 : regularInvoice.totalTax,
        fromRecurring: regularInvoice.recurringId !== undefined,
        isEUCustomer: regularInvoice.isEUCustomer,
        // isDomesticCustomer is deliberately not set here: the invoice API model carries no customer
        // country. CustomerInformationLoader sets it from the customer it already loads on open, and
        // snaps any drifted locked row back to its article's VAT once both it and the company's
        // opt-in flag are known. The rows' half of the lock is hydrated below.
        products: regularInvoice.rows
          ?.sort((p1, p2) => p1.orderIndex - p2.orderIndex)
          .map((product): InvoiceFormTypes.Product => {
            const standardPrice = product.overriddenUnitPrice ?? product.unitPrice ?? 0;
            const vat = noVat ? 0 : product.vat;
            return {
              id: product.id,
              uniqueId: product.id ? product.id : UtilsHelper.getUniqueId(),
              article: { id: product.articleId, value: product.description ?? product.overriddenDescription ?? "" },
              quantity: product.quantity,
              text: product.overriddenDescription ?? product.description,
              price: standardPrice,
              unit: product.unit?.id,
              account: product.account,
              // The article's *current* configuration (InvoiceRow.articleAccount/articleVat/
              // articleIsAgentSale, Core #13496), distinct from `account` above which is the value
              // stamped on this row at send. Hydrating them lets the VAT lock apply to an opened
              // invoice, including "kreditera och redigera"; a free-text row comes back with all
              // three unset and stays unlocked. Undefined until that Core version is deployed.
              articleAccount: product.articleAccount,
              articleVat: product.articleVat,
              articleIsAgentSale: product.articleIsAgentSale,
              tax: vat,
              taxText:
                vat === 0
                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)
                  : `${product.vat}%`,
              isEUCustomer: invoice.isEUCustomer,
              eUVatCategoryId: product.euVatProductType,
              dimensions: product.dimensions
                ? product.dimensions
                    .filter((d) => allowedDimensionIds.includes(d))
                    .concat(
                      invoice.dimensions?.filter(
                        (d) => allowedDimensionIds.includes(d) && !product.dimensions?.includes(d)
                      ) ?? []
                    )
                : invoice.dimensions?.filter((d) => allowedDimensionIds.includes(d)),
              periodisation: (() => {
                const hasDateFields =
                  product.periodisationStartDate !== undefined && product.periodisationStartDate !== null;
                if (hasDateFields) {
                  return {
                    isEnabled: product.periodisation,
                    mode: "date" as const,
                    range: {
                      start: product.periodisationStartDate
                        ? DateHelper.parseDateOnly(product.periodisationStartDate as unknown as string)
                        : undefined,
                      end: product.periodisationEndDate
                        ? DateHelper.parseDateOnly(product.periodisationEndDate as unknown as string)
                        : undefined,
                    },
                  };
                }
                const hasPeriodisationData =
                  (product.periodisationStartYear !== undefined || product.periodisationStartYear !== null) &&
                  (product.periodisationStartMonth !== undefined || product.periodisationStartMonth !== null) &&
                  (product.periodisationEndYear !== undefined || product.periodisationEndYear !== null) &&
                  product.periodisationEndMonth !== undefined &&
                  product.periodisationEndMonth !== null &&
                  (product.periodisationStartDate !== undefined || product.periodisationStartDate !== null) &&
                  (product.periodisationEndDate !== undefined || product.periodisationEndDate !== null);
                return {
                  isEnabled: product.periodisation,
                  mode: "month" as const,
                  range:
                    hasPeriodisationData && (product.periodisation || regularInvoice.fromRecurring)
                      ? {
                          start: DateHelper.getDateFromYearMonth(
                            (product.periodisationStartYear ?? 0) * 100 + (product.periodisationStartMonth ?? 0)
                          ),
                          end: DateHelper.getDateFromYearMonth(
                            (product.periodisationEndYear ?? 0) * 100 + (product.periodisationEndMonth ?? 0)
                          ),
                        }
                      : undefined,
                };
              })(),
            };
          }),
        currency: invoice.currency ?? CurrencyEnums.CurrencyCode.SEK,
        isPeriodisationAdvancedConfiguration: regularInvoice.rows?.some((row, index, arr) => {
          const r = row as typeof row & { periodisationStartDate?: string; periodisationEndDate?: string };
          const first = arr[0] as (typeof arr)[0] & { periodisationStartDate?: string; periodisationEndDate?: string };
          return (
            `${row.periodisation}${row.periodisationStartYear}${row.periodisationStartMonth}${row.periodisationEndYear}${row.periodisationEndMonth}${r.periodisationStartDate ?? ""}${r.periodisationEndDate ?? ""}` !==
            `${first.periodisation}${first.periodisationStartYear}${first.periodisationStartMonth}${first.periodisationEndYear}${first.periodisationEndMonth}${first.periodisationStartDate ?? ""}${first.periodisationEndDate ?? ""}`
          );
        }),
        isPeriodisationByDate: regularInvoice.rows?.some((row) => {
          const r = row as typeof row & { periodisationStartDate?: string };
          return r.periodisationStartDate !== undefined && r.periodisationStartDate !== null;
        }),
        isDimensionsAdvancedConfiguration: hasInvoiceRowDimensions,
        otherSettings: {
          ...obj.otherSettings,
          invoiceDate: new Date(regularInvoice.postingDate),
          deliveryDate: new Date(regularInvoice.deliveryDate),
          paymentTerms: regularInvoice.paymentTerms,
          dueDate: new Date(regularInvoice.dueDate),
          notes: regularInvoice.notes,
          attachments: regularInvoice.attachments as UploadedFile[],
          invoiceLayout: invoice.template ?? defaultInvoiceLayout,
          languageKey: invoice.language,
          showCurrencyCode: invoice.showCurrencyCode,
          showReferenceInAddress: invoice.showReferencePersonInAddress,
          showCustomerDeliveryAddress: invoice.showShippingAddress,
          sellerName: invoice.contactPerson,
          paymentMethodId: invoice.paymentOptionId,
          penaltyInterestType:
            regularInvoice.penaltyInterest?.type ?? WintSuperkollAPIModel.InvoicePenaltyInterestType.NoPenaltyInterest,
          penaltyInterestPercentage:
            regularInvoice.penaltyInterest?.rate !== undefined ? regularInvoice.penaltyInterest.rate * 100 : undefined,
          reminderCost: regularInvoice?.reminderCost,
          isAutomaticRemindersEnabled: regularInvoice?.isAutomaticRemindersEnabled,
          automaticReminderPeriod: regularInvoice?.automaticReminderPeriod,
          automaticReminderDelay: regularInvoice?.automaticReminderDelay,
          automaticReminderAmountLimit: regularInvoice?.automaticReminderAmountLimit,
          automaticReminderCountLimit: regularInvoice?.automaticReminderCountLimit,
          automaticReminderNotes: regularInvoice?.automaticReminderNotes,
          automaticReminderSendType:
            regularInvoice?.automaticReminderSendMethod ?? WintSuperkollAPIModel.InvoiceSendMethod.Print,
          automaticReminderSendMail:
            regularInvoice?.automaticReminderSendMethod === WintSuperkollAPIModel.InvoiceSendMethod.Email
              ? {
                  emailFromType: regularInvoice?.automaticReminderMailFromType,
                  emailTo: regularInvoice?.automaticReminderMailToAddresses
                    ? UtilsHelper.splitMultipleEmails(regularInvoice.automaticReminderMailToAddresses).map((email) => ({
                        id: email,
                        value: email,
                      }))
                    : [],
                  emailSubject: regularInvoice?.automaticReminderMailSubject,
                  emailMessage: regularInvoice?.automaticReminderMailMessage,
                }
              : {},
        },
        rutRot: {
          ...obj.rutRot,
          houseWorkType: regularInvoice.invoiceHouseWorkType,
          rutRotPersonNumber: regularInvoice.customerPersonNumber?.replace("-", ""),
          rotPropertyType: regularInvoice.invoiceRotPropertyType,
          propertyDesignation: regularInvoice.propertyDesignation,
          tenantsOrgNumber: regularInvoice.tenantsOwnerAssociationOrgNumber?.replace("-", ""),
          apartmentNumber: regularInvoice.apartmentNumber?.replace("-", ""),
          houseWorkOtherCosts: regularInvoice.houseWorkOtherCosts
            ? Number(((+regularInvoice.houseWorkOtherCosts * 100) / (100 + +houseWorkOtherCostsVAT)).toFixed(2))
            : 0,
          rutRotArticles: regularInvoice.rows
            ?.filter((r) => r.rot || r.rut)
            .map(
              (r): InvoiceFormTypes.RutRotArticleItem => ({
                id: r.id,
                name: r.description ?? r.overriddenDescription,
                materialCost: r.materialCost ? Number(((+r.materialCost * 100) / (100 + +r.vat)).toFixed(2)) : 0,
                typeOfWorkId: r.houseWorkTypeId,
                noOfHours: r.houseWorkHours,
              })
            ),
        },
      };
    }
    return obj;
  };

  return {
    getDraftInitialValues,
    getInvoiceInitialValues,
    getRecurringInvoiceInitialValues,
  };
}

export default useInvoiceInitialValues;
